import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Toggle from "../Toggle/Toggle";
import Pricing from "../../../data/Pricing";
import { capitalizeFirstLetter } from "../../../utils/utils";
import styles from "./ProductCard.module.css";
import semaglutideSuspensionImage from "../../../assets/semaglutide-suspension.webp";
import semaglutideInjectableImage from "../../../assets/semaglutide-injectable.webp";
import tirzepatideSuspensionImage from "../../../assets/tirzepatide-suspension.webp";
import tirzepatideInjectableImage from "../../../assets/tirzepatide-injectable.webp";
import { trackPostHogEvent, registerPostHogProperty } from "../../../utils/posthogUtils";


export default function ProductCard({ medication, defaultFrequency, defaultDosageForm }) {

    const [ dosageForm, setDosageForm ] = useState(defaultDosageForm || 'injectable'); // Default to injectable
    const [ selectedFrequency, setSelectedFrequency ] = useState(defaultFrequency || 'monthly'); // Default to monthly
    const [ currentPricingDetails, setCurrentPricingDetails ] = useState(null);
    const [ currentRetailPricingDetails, setCurrentRetailPricingDetails ] = useState(null);
    const [ monthlySavingsLabel, setMonthlySavingsLabel ] = useState('');
    const [ quarterlySavingsLabel, setQuarterlySavingsLabel ] = useState('');
    const [ displayedPlanPrice, setDisplayedPlanPrice ] = useState(null);
    const [ thirdMonthFree, setThirdMonthFree ] = useState(false);
    const [ isQuarterlyPlan, setIsQuarterlyPlan ] = useState(selectedFrequency === 'threeMonths');
    const [ isMonthlyPlan, setIsMonthlyPlan ] = useState(selectedFrequency === 'monthly');

    const pricing = Pricing();
    const isWholeSale = pricing.isWholesale();
    const pricingOptions = pricing.getPricingOptionsByProductName(medication.toLowerCase());
    const retailPricingOptions = pricing.getRetailPrices()[medication.toLowerCase()];
    
    // Handle plan selection and navigate to checkout
    const navigate = useNavigate();
    
    const handlePlanSelection = () => {
        if (currentPricingDetails && currentPricingDetails.id) {
            localStorage.setItem('plan_id', currentPricingDetails.id);
            registerPostHogProperty('initially_selected_frequency', selectedFrequency);
            trackPostHogEvent('plan_selected', {
                product: medication.toLowerCase(),
                dosage_form: dosageForm,
                frequency: selectedFrequency,
                plan_id: currentPricingDetails.id
            });
            console.log("Selected plan ID stored in localStorage:", currentPricingDetails.id);
            navigate('/checkout/payment');
        }
    }

    // Get the current pricingDetails based on dosage form, and frequency selected
    useEffect(() => {
        if (!medication || !dosageForm || !selectedFrequency || !pricingOptions) return;

        const pricingDetails = pricingOptions?.[dosageForm]?.[selectedFrequency] ?? null;
        setCurrentPricingDetails(pricingDetails);

        const hasFirstQuarterDiscount = selectedFrequency === 'threeMonths' && Boolean(pricingDetails?.first_quarter_price_id && pricingDetails?.first_quarter_price);
        setDisplayedPlanPrice(hasFirstQuarterDiscount ? pricingDetails?.first_quarter_price : pricingDetails?.total);
        setIsQuarterlyPlan(selectedFrequency === 'threeMonths');
        setIsMonthlyPlan(selectedFrequency === 'monthly');

        setThirdMonthFree(Boolean(pricingOptions?.[dosageForm]?.monthly?.third_month_price_id));

        if (isWholeSale && retailPricingOptions) {
            const retailPricingDetails = retailPricingOptions?.[dosageForm] ?? null;
            setCurrentRetailPricingDetails(retailPricingDetails?.[selectedFrequency] ?? null);

            const retailQuarterlyPlan = retailPricingDetails?.threeMonths ?? null;
            const discountedQuarterlyPlan = pricingOptions?.[dosageForm]?.threeMonths ?? null;
            if (discountedQuarterlyPlan?.first_quarter_price) {
                setQuarterlySavingsLabel(`Save $${pricing.calculateDiscount(retailQuarterlyPlan?.total, discountedQuarterlyPlan?.first_quarter_price)} off retail`);
            } else {
                setQuarterlySavingsLabel(`Save $${pricing.calculateDiscount(retailQuarterlyPlan?.total, discountedQuarterlyPlan?.total)} off retail`);
            }

            const retailMonthlyPlan = retailPricingDetails?.monthly ?? null;
            const discountedMonthlyPlan = pricingOptions?.[dosageForm]?.monthly ?? null;

            if (pricingOptions?.[dosageForm]?.monthly?.third_month_price_id) {
                setMonthlySavingsLabel('Third month FREE');
            }
            else {
                setMonthlySavingsLabel(`Save $${pricing.calculateDiscount(retailMonthlyPlan?.total, discountedMonthlyPlan?.total)} off retail`);
            }

        } else {
            setCurrentRetailPricingDetails(null);
            setQuarterlySavingsLabel(medication === "Semaglutide" ? "Save 16%" : "Save 14%");
            setMonthlySavingsLabel(null);
        }
    }, [
        medication,
        dosageForm,
        selectedFrequency,
        pricingOptions,
        isWholeSale,
        retailPricingOptions
    ]);

    return (
        <div className={styles.resultCardContainer}>
            <div className={styles.resultCardContent}>
                <div className={styles.medicationImageContainer}>
                    <img
                        src={medication === "Semaglutide" ? (dosageForm === "suspension" ? semaglutideSuspensionImage : semaglutideInjectableImage) : (dosageForm === "suspension" ? tirzepatideSuspensionImage : tirzepatideInjectableImage)}
                        alt={medication}
                        className={styles.medicationImage}
                    />
                </div>
                <div className={styles.detailsContainer}>
                    <div className={styles.textContainer}>
                        <div className={styles.titleAndPriceRow}>
                            <span className={styles.title}>{medication} {capitalizeFirstLetter(dosageForm)}*</span>
                            <div className={styles.priceColumn}>
                                {!isWholeSale && (
                                    <div className={styles.price}>
                                        <span>${displayedPlanPrice?.split('.')[0]}</span>
                                        <span className={styles.cents}>.{displayedPlanPrice?.split('.')[1]}</span>
                                    </div>
                                )}
                                {isWholeSale && (
                                    <div className={styles.wholesalePricingContainer}>
                                        <div className={styles.pricesColumn}>
                                            <div className={styles.discountedPriceRow}>
                                                <span className={styles.retailPrice}>${currentRetailPricingDetails?.total.split('.')[0]}</span>
                                                <span className={styles.wholesalePrice}>${displayedPlanPrice?.split('.')[0]}</span>
                                            </div>
                                        </div>
                                        {isQuarterlyPlan && currentPricingDetails?.first_quarter_price && (
                                            <div className={styles.discountContainerWrapper}>
                                                <div className={styles.discountContainer}>
                                                    <div className={styles.youSaveText}>Renews at ${currentPricingDetails?.total.split('.')[0]} </div>
                                                </div>
                                            </div>
                                        )}
                                        {thirdMonthFree && isMonthlyPlan && (
                                            <div className={styles.discountContainerWrapper}>
                                                <div className={styles.discountContainer}>
                                                    <div className={styles.youSaveText}>Third month FREE!</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.weightLossPercentage}>
                            <div className={styles.infoIconContainer}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 15H11V9H9V15ZM10.713 6.713C10.9043 6.521 11 6.28333 11 6C11 5.71667 10.904 5.47933 10.712 5.288C10.52 5.09667 10.2827 5.00067 10 5C9.71733 4.99933 9.48 5.09533 9.288 5.288C9.096 5.48067 9 5.718 9 6C9 6.282 9.096 6.51967 9.288 6.713C9.48 6.90633 9.71733 7.002 10 7C10.2827 6.998 10.5203 6.902 10.713 6.712M10 20C8.61667 20 7.31667 19.7373 6.1 19.212C4.88334 18.6867 3.825 17.9743 2.925 17.075C2.025 16.1757 1.31267 15.1173 0.788001 13.9C0.263335 12.6827 0.000667933 11.3827 1.26582e-06 10C-0.000665401 8.61733 0.262001 7.31733 0.788001 6.1C1.314 4.88267 2.02633 3.82433 2.925 2.925C3.82367 2.02567 4.882 1.31333 6.1 0.788C7.318 0.262667 8.618 0 10 0C11.382 0 12.682 0.262667 13.9 0.788C15.118 1.31333 16.1763 2.02567 17.075 2.925C17.9737 3.82433 18.6863 4.88267 19.213 6.1C19.7397 7.31733 20.002 8.61733 20 10C19.998 11.3827 19.7353 12.6827 19.212 13.9C18.6887 15.1173 17.9763 16.1757 17.075 17.075C16.1737 17.9743 15.1153 18.687 13.9 19.213C12.6847 19.739 11.3847 20.0013 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z" fill="#4E4E4E" fill-opacity="0.992157"/>
                                </svg>
                            </div>

                            <span>{
                                dosageForm === "suspension" ?
                                    "Your provider may recommend an oral therapy when appropriate for your health history and goals"
                                    : "Your provider may recommend an injectable therapy when appropriate for your health history and goals"
                            }</span>
                        </div>
                    </div>
                    <div className={styles.dosageFormContainer}>
                        <span className={styles.dosageFormLabel}>Dosage form</span>
                        <Toggle
                            options={[{ label: "Injectable", value: "injectable" }, { label: "Oral", value: "suspension" }]}
                            value={dosageForm}
                            onChange={setDosageForm}
                        />
                    </div>
                    <div className={styles.frequencyContainer}>
                        <span className={styles.frequencyLabel}>Administration frequency</span>
                        <Toggle
                            options={[
                                {
                                    label: <>Monthly<br /><span className={styles.save}>{monthlySavingsLabel}</span></>,
                                    value: "monthly"
                                },
                                { 
                                    value: "threeMonths",
                                    label: <>Quarterly<br /><span className={styles.save}>{quarterlySavingsLabel}</span></>
                                }
                            ]}
                            value={selectedFrequency}
                            onChange={setSelectedFrequency}
                        />
                    </div>
                    <div className={styles.additionalInfo}>
                        {selectedFrequency === "monthly" ?
                            <span className={styles.note}>4 week supply. Billed every 28 days.
                            Cancel at any time. No hidden fees.
                            {thirdMonthFree && ' Third month FREE with monthly subscription. '}
                            
                            </span>
                            :
                            <span className={styles.note}>12 week supply. Billed every 84 days. Cancel at any time. No hidden fees.</span>
                        }
                        <span className={styles.note}></span>
                    </div>
                </div>
            </div>
            <button className={styles.selectPlanButton} onClick={handlePlanSelection}>Select Plan</button>
        </div>
    );
}